// ---------------------------------------------------------------------------
// WorkspaceContext — single source of truth for workspace state.
//
// Holds:
//  - the *active* workspace name (in-memory; resets on reload).
//  - the *list* of workspaces fetched from `GET /Workspaces`, with a shared
//    `refresh()` so every consumer (top-bar switcher, manage drawer, create
//    flow) sees the same data and can trigger a refetch that propagates.
//
// The merged shape replaces an earlier split between `WorkspaceProvider`
// (active name) and `WorkspacesProvider` (list); the singular/plural pair
// was confusing and almost always consumed together via `useWorkspaces`.
// ---------------------------------------------------------------------------

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { PropsWithChildren } from 'react';

import { useHttpClient } from '../api/HttpClientContext.js';
import { DEFAULT_WORKSPACE_NAME } from '../constants/index.js';
import { WorkspaceService } from '../services/workspaceService.js';
import type { WorkspaceSummary } from '../types/workspace.js';

interface WorkspaceContextValue {
  activeName: string | null;
  setActiveName: (name: string | null) => void;
  workspaces: WorkspaceSummary[];
  isLoading: boolean;
  error: string | null;
  /**
   * Refetch the workspace list. Returns a promise that resolves once state
   * has been updated, so callers (e.g. CreateView after a successful POST)
   * can `await refresh()` before flipping any selection that depends on the
   * new entry being present.
   */
  refresh: () => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function WorkspaceProvider({ children }: PropsWithChildren) {
  const httpClient = useHttpClient();
  const service = useMemo(() => new WorkspaceService(httpClient), [httpClient]);

  const [activeName, setActiveNameState] = useState<string | null>(
    DEFAULT_WORKSPACE_NAME,
  );
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Sequence number for in-flight fetches: an older response can't overwrite
  // a newer one if `refresh()` is called multiple times in quick succession.
  const fetchIdRef = useRef(0);

  const setActiveName = useCallback((name: string | null) => {
    setActiveNameState(name);
  }, []);

  const refresh = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const items = await service.list();
      if (fetchId === fetchIdRef.current) {
        setWorkspaces(items);
      }
    } catch (err) {
      if (fetchId === fetchIdRef.current) {
        setError(
          err instanceof Error ? err.message : 'Failed to load workspaces',
        );
      }
    } finally {
      if (fetchId === fetchIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [service]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      activeName,
      setActiveName,
      workspaces,
      isLoading,
      error,
      refresh,
    }),
    [activeName, setActiveName, workspaces, isLoading, error, refresh],
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspaceContext(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error(
      'useWorkspaceContext must be used within <WorkspaceProvider>',
    );
  }
  return ctx;
}
